import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import pDebounce from "p-debounce";

import { Actions as PEOPLES_PENSIONS_ACTIONS } from "./store/peoplesPensionActions";
import { INTEGRATION_LOGO, INTEGRATION_NAME, INTEGRATION_IN_PROGRESS as unfinished } from "./store/config";

import {
	ArrowDownTrayIcon,
	ArrowPathIcon,
	BuildingOffice2Icon,
	ChevronDownIcon,
	Cog8ToothIcon,
	MinusIcon,
} from "@heroicons/react/20/solid";
import AsyncSelect from "react-select/async";
import ReactSelect from "react-select";
import { CustomOption } from "../../common/custom-selectors/CustomOption";
import UserConnectedButton from "../UserConnectedButton";
import PeoplesPensionSchemeImport from "./PeoplesPensionSchemeImport";

const PeoplesPensionSettings = ({
	connected,
	disconnect,
	userConnected,
	connectionId,
	disabled,
	companyName,
	organisations,
	getPeoplesOrganisations,
	switchingOrganisation,
	savePeoplesPensionSettings,
	loadingOrganisations,
	updatePeoplePensionConnectedOrg,
	connect,
	getPeoplesPensionSchemes,
	importingSchemes,
	importSchemes,
	pensionPayScheduleOptions,
	paySchedules,
	settings,
	saving,
}) => {
	const [expand, setExpand] = useState(false);
	const [PeoplesPensionSettings, setPeoplesPensionSettings] = useState({});

	useEffect(() => {
		if (!connected && expand) setExpand(false);
		if (!expand || !connectionId) return;

		getPeoplesPensionSchemes();
	}, [connected, expand, connectionId, !pensionPayScheduleOptions]);

	useEffect(() => {
		setPeoplesPensionSettings(settings || {});
	}, [JSON.stringify(settings)]);

	const getOrgList = pDebounce(getPeoplesOrganisations, 500);

	const placeholder = useMemo(() => {
		if (switchingOrganisation) return "Linking Company...";
		if (!companyName) return;

		return (
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<div>{companyName}</div>
				<BuildingOffice2Icon style={{ height: "2rem", display: "block", paddingRight: "2rem" }} />
			</div>
		);
	}, [switchingOrganisation, companyName]);

	const updateSetting = ({ category, field, value }) => {
		setPeoplesPensionSettings({
			...PeoplesPensionSettings,
			[category]: {
				...PeoplesPensionSettings?.[category],
				[field]: value,
			},
		});
	};

	return (
		<div className={`${expand ? "tr-gray" : ""} col-xs-12 integration-row`}>
			<div className="col-xs-6">
				<div
					style={{
						display: "inline-block",
						width: 40,
						height: 40,
						padding: 2.5,
						background: "white",
						borderRadius: 5,
					}}
				>
					<img
						src={INTEGRATION_LOGO}
						width="35px"
						style={{ marginTop: 7 }}
					/>
				</div>

				<span style={{ paddingLeft: 20, fontSize: 16 }}>{INTEGRATION_NAME}</span>
			</div>
			<div className="col-xs-6">
				<div className="btn-group">
					{userConnected ? <UserConnectedButton integration={userConnected} connectUser={connect} /> : null}
					{connected ? <div className="btn btn-default">Connected</div> : null}
					{unfinished && !connected ? (
						<div className="btn btn-default pull-right">Coming Soon</div>
					) : (
						<>
							<button
								className={`btn btn-${connected ? "default" : "primary"} pull-right`}
								onClick={() => {
									if (connected) setExpand(!expand);
									else connect();
								}}
							>
								{connected ? (
									<>
										<Cog8ToothIcon style={{ height: "1.5em", float: "left" }} />
										<div style={{ float: "left", marginLeft: 5 }}>Settings</div>
										<ChevronDownIcon
											style={{
												height: "1.5em",
												float: "left",
												transform: expand ? "rotate(180deg)" : "rotate(0deg)",
												transition: "transform 0.2s ease-in-out",
											}}
										/>
									</>
								) : (
									"Connect"
								)}
							</button>
						</>
					)}
				</div>
			</div>

			<div className="clearfix" />
			{expand && connected ? (
				<>
					<div
						className="row"
						style={{ paddingBottom: 0, paddingTop: "3rem" }}
					>
						<div className="col-xs-6 pr-0 pl-0">
							<div className="form-material form-container">
								<label htmlFor="material-text">Company Link</label>
								<AsyncSelect
									isDisabled={disabled || switchingOrganisation}
									isLoading={loadingOrganisations || switchingOrganisation}
									name="company"
									defaultOptions={organisations}
									menuPlacement="auto"
									components={{ Option: CustomOption }}
									classNamePrefix="react-select-search"
									placeholder={placeholder}
									onMenuOpen={getOrgList}
									value={null}
									onChange={(selected) => {
										updatePeoplePensionConnectedOrg(selected.label, selected.value);
									}}
								/>
							</div>
						</div>
						<div className="col-xs-6 pr-0 pl-0">
							<button
								style={{ marginTop: 13 }}
								className="btn btn-primary pull-right"
								onClick={() => {
									disconnect();
								}}
							>
								<MinusIcon style={{ height: "1.5em", float: "left" }} />
								<div style={{ float: "left", marginLeft: 5 }}>Disconnect</div>
							</button>
							<button
								style={{ marginTop: 13, marginRight: 10 }}
								className="btn btn-default pull-right"
								disabled={importingSchemes}
								onClick={() => {
									importSchemes();
								}}
							>
								{importingSchemes ? (
									<ArrowPathIcon
										style={{ height: "1.5em", float: "left" }}
										className="animate-spin"
									/>
								) : (
									<ArrowDownTrayIcon style={{ height: "1.5em", float: "left" }} />
								)}
								<div style={{ float: "left", marginLeft: 5 }}>
									Import{importingSchemes ? "ing" : null} Schemes
								</div>
							</button>
						</div>
						<div className="clearfix" />

						{pensionPayScheduleOptions?.length ? (
							<>
								<div className="form-inner-section px-0 mx-0">
									<div
										className="form-section-header"
										style={{ marginLeft: "1.5rem", marginBottom: "2rem" }}
									>
										<h4>Pay Schedule Settings</h4>
									</div>
									{pensionPayScheduleOptions?.map((option) => {
										return (
											<>
												<div className="col-xs-6">
													<div
														className="form-material"
														style={{ marginBottom: "2rem" }}
													>
														<label htmlFor="material-text">{INTEGRATION_NAME}</label>
														<ReactSelect
															value={option}
															isDisabled={true}
														/>
													</div>
												</div>
												<div className="col-xs-6">
													<div
														className="form-material"
														style={{ marginBottom: "2rem" }}
													>
														<label htmlFor="material-text">Buddy</label>
														<ReactSelect
															options={paySchedules}
															value={paySchedules?.find(
																({ value }) =>
																	value == settings?.paySchedules?.[option?.value],
															)}
															isDisabled={disabled}
															onChange={(selected) => {
																updateSetting({
																	category: "paySchedules",
																	field: option?.value,
																	value: selected?.value,
																});
															}}
														/>
													</div>
												</div>
											</>
										);
									})}
									<button
										style={{ marginRight: "2rem" }}
										className="btn btn-success pull-right"
										onClick={() => {
											console.debug("save", PeoplesPensionSettings);
											savePeoplesPensionSettings(PeoplesPensionSettings);
										}}
										disabled={saving}
									>
										Save
									</button>
									<div className="clearfix" />
								</div>
							</>
						) : null}

						<PeoplesPensionSchemeImport />
					</div>
				</>
			) : null}
			<div className="clearfix" />
		</div>
	);
};

const mapStateToProps = (state) => {
	const { peoplesPension } = { ...state.integration?.pension };
	const {
		settings,
		companyName,
		organisations,
		loadingOrganisations,
		switchingOrganisation,
		connectionId,
		importingSchemes,
		pensionPayScheduleOptions,
		saving,
	} = { ...peoplesPension };

	return {
		userConnected: state.my?.integration?.pension?.peoplesPension,
		connected: !!peoplesPension,
		companyName,
		connectionId,
		switchingOrganisation,
		loadingOrganisations,
		organisations,
		saving,
		importingSchemes,
		settings,
		jurisdiction: state.company?.settings?.jurisdiction,
		adminAccounts: state.integration?.pensions?.peoplesPension?.adminAccounts,
		pensionPayScheduleOptions,
		paySchedules: state.company?.settings?.paySchedules?.map((schedule) => ({
			value: schedule.id,
			label: schedule.name,
		})),
	};
};

const mapDispatchToProps = (dispatch) => ({
	connect: () => dispatch(PEOPLES_PENSIONS_ACTIONS.connectPeoplesPension()),
	disconnect: () => dispatch(PEOPLES_PENSIONS_ACTIONS.disconnectPeoplesPension()),
	savePeoplesPensionSettings: (body) => dispatch(PEOPLES_PENSIONS_ACTIONS.savePeoplesPensionSettings(body)),
	getPeoplesOrganisations: () => dispatch(PEOPLES_PENSIONS_ACTIONS.getPeoplesPensionOrganisations()),
	updatePeoplePensionConnectedOrg: (companyName, organisationId) =>
		dispatch(PEOPLES_PENSIONS_ACTIONS.updatePeoplePensionConnectedOrg({ companyName, organisationId })),
	getPeoplesPensionSchemes: () => dispatch(PEOPLES_PENSIONS_ACTIONS.getPeoplesPensionSchemes()),
	importSchemes: () => dispatch(PEOPLES_PENSIONS_ACTIONS.importPensionSchemes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PeoplesPensionSettings);
